import { useState } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";

interface CancellationMessageChoiceProps {
  /** The patient's name as the surrounding surface shows it. */
  who?: string;
  /** `null` until staff pick; there is deliberately no default answer. */
  value: boolean | null;
  onChange: (send: boolean) => void;
  disabled?: boolean;
}

/**
 * "Send cancellation message to …? Yes / No" — the question a practice set to "Ask each
 * time" puts on every cancel and delete. Nothing is preselected, so the surface's submit
 * stays disabled until staff answer.
 *
 * Both buttons keep the same width in either state (only colours change), so picking one
 * never reflows the row under the pointer.
 */
export function CancellationMessageChoice({
  who,
  value,
  onChange,
  disabled
}: CancellationMessageChoiceProps) {
  const options = [
    { send: true, label: "Yes, send" },
    { send: false, label: "No, don't send" }
  ];

  return (
    <Box
      role="group"
      aria-labelledby="cancellation-message-choice-title"
      data-testid="cancellation-message-choice"
      sx={{
        width: "100%",
        boxSizing: "border-box",
        textAlign: "left",
        border: 1,
        borderColor: "primary.main",
        borderRadius: 1,
        p: 1.5,
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06)
      }}
    >
      <Typography
        id="cancellation-message-choice-title"
        variant="body2"
        fontWeight={700}
      >
        {who
          ? `Send cancellation message to ${who}?`
          : "Send the patient a cancellation message?"}
      </Typography>
      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ mt: 0.5, mb: 1.25 }}
      >
        Uses your practice&apos;s Appointment Cancelled template.
      </Typography>
      <Stack direction="row" gap={1}>
        {options.map((option) => {
          const selected = value === option.send;
          return (
            <Button
              key={option.label}
              type="button"
              fullWidth
              disableElevation
              disabled={disabled}
              aria-pressed={selected}
              data-testid={`cancellation-message-${option.send ? "yes" : "no"}`}
              variant={selected ? "contained" : "outlined"}
              onClick={() => onChange(option.send)}
              sx={{
                minHeight: 44,
                bgcolor: selected ? undefined : "background.paper"
              }}
            >
              {option.label}
            </Button>
          );
        })}
      </Stack>
      {value === null && (
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
          Choose Yes or No to continue.
        </Typography>
      )}
    </Box>
  );
}

/**
 * The same question as the body of the shared confirm dialog, which keeps its `content`
 * in Redux and so can never re-render a controlled input. This one owns its answer and
 * reports it through `onPick` (the `DeleteScopeContent` pattern).
 */
export function CancellationMessageConfirmContent({
  body,
  who,
  onPick
}: {
  /** What the dialog said before this question existed; shown above it unchanged. */
  body?: React.ReactNode;
  who?: string;
  onPick: (send: boolean) => void;
}) {
  const [value, setValue] = useState<boolean | null>(null);

  return (
    <Stack gap={2} sx={{ width: "100%" }}>
      {body ? <Box>{body}</Box> : null}
      <CancellationMessageChoice
        who={who}
        value={value}
        onChange={(send) => {
          setValue(send);
          onPick(send);
        }}
      />
    </Stack>
  );
}
